import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard.tsx';
import ProjectsList from './components/ProjectsList.tsx';
import UsersList from './components/UsersList.tsx';
import Analytics from './components/Analytics.tsx';
import ProjectDetails from './components/ProjectDetails.tsx';
import UserDetails from './components/UserDetails.tsx';
import CreditConfigPage from './components/CreditConfigPage.tsx';

type View = 'dashboard' | 'projects' | 'users' | 'analytics' | 'credit-config' | 'project-details' | 'user-details';

const App = () => {
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [apiHealthy, setApiHealthy] = useState<boolean | null>(null);

  useEffect(() => {
    checkApiHealth();
  }, []);

  const checkApiHealth = async () => {
    try {
      // Replace with your actual health check endpoint
      const response = await fetch('/api/billing/health');
      setApiHealthy(response.ok);
    } catch (error) {
      console.error('API health check failed:', error);
      setApiHealthy(false);
    }
  };

  const handleViewProject = (projectId: string) => {
    setSelectedProjectId(projectId);
    setCurrentView('project-details');
  };

  const handleViewUser = (userId: string) => {
    setSelectedUserId(userId);
    setCurrentView('user-details');
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Navigation */}
      <nav className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-xl font-bold text-white">Billing Analytics</h1>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {[
                  { key: 'dashboard', label: 'Dashboard' },
                  { key: 'projects', label: 'Projects' },
                  { key: 'users', label: 'Users' },
                  { key: 'analytics', label: 'Analytics' },
                  { key: 'credit-config', label: 'Credit Configuration' }
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setCurrentView(key as View)}
                    className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors duration-200 ${
                      currentView === key
                        ? 'border-blue-400 text-white'
                        : 'border-transparent text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* API Health Indicator */}
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  apiHealthy === null ? 'bg-yellow-400' : 
                  apiHealthy ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
                <span className="text-sm text-gray-400">
                  API: {apiHealthy === null ? 'Checking...' : apiHealthy ? 'Online' : 'Offline'}
                </span>
                {apiHealthy === false && (
                  <button
                    onClick={checkApiHealth}
                    className="text-xs text-blue-400 hover:text-blue-300 ml-2"
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="sm:hidden flex items-center">
              <select 
                value={currentView}
                onChange={(e) => setCurrentView(e.target.value as View)}
                className="bg-gray-800 text-white border border-gray-600 rounded px-3 py-1"
              >
                <option value="dashboard">Dashboard</option>
                <option value="projects">Projects</option>
                <option value="users">Users</option>
                <option value="analytics">Analytics</option>
                <option value="credit-config">Credit Config</option>
              </select>
            </div>
          </div>
        </div>
      </nav>

      {/* API Connection Warning */}
      {apiHealthy === false && (
        <div className="bg-red-900 border-b border-red-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-200 text-sm">
                Unable to connect to the API server. Please ensure the backend service is running
              </span>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {currentView === 'dashboard' && <Dashboard onViewProject={handleViewProject} onViewUser={handleViewUser} />}
        {currentView === 'projects' && <ProjectsList onViewProject={handleViewProject} />}
        {currentView === 'users' && <UsersList onViewUser={handleViewUser} />}
        {currentView === 'analytics' && <Analytics />}
        {currentView === 'credit-config' && <CreditConfigPage />}
        {currentView === 'project-details' && (
          <ProjectDetails 
            projectId={selectedProjectId} 
            onBack={() => setCurrentView('projects')}
          />
        )}
        {currentView === 'user-details' && (
          <UserDetails 
            userId={selectedUserId} 
            onBack={() => setCurrentView('users')}
            onViewProject={handleViewProject}
          />
        )}
      </main>
    </div>
  );
};

export default App;